import moment from 'moment';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActivityRow,
	Avatar,
	CircleRemoveIcon,
	HelpIcon,
	PencilIcon,
	SyncIcon,
	UndoIcon,
	UserCheckIcon,
	UserPlusIcon,
	WarningIcon,
	tokens,
} from '@treams/treams-ui';

import useScoreOptions from '../hooks/useScoreOptions';
import { type RetentionRiskLevel, retentionRiskLabelKey } from '../retentionRisk';

const AVATAR_SIZE = 24;

const Name = styled.span`
	font-weight: ${tokens.typography.weights.medium};
`;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const emphasizeNames = (sentence: string, names: string[]) => {
	const unique = [...new Set(names.filter(Boolean))].sort((a, b) => b.length - a.length);
	if (unique.length === 0) return sentence;

	const pattern = new RegExp(`(${unique.map(escapeRegExp).join('|')})`, 'g');
	return sentence
		.split(pattern)
		.map((part, index) => (unique.includes(part) ? <Name key={index}>{part}</Name> : part));
};

type Item = graphql.TalentReviewActivityFragment;

type ActivityCopy = {
	text: string;
	marker: ReactNode;
	details?: ReactNode;
};

const JUST_NOW_SECONDS = 45;

const name = (user?: { firstName: string; lastName: string } | null) =>
	user ? `${user.firstName} ${user.lastName}` : '';

const activityCopy = (options: {
	item: Item;
	t: ReturnType<typeof useTranslation>['t'];
	actor: string;
	user: string;
	successor: string;
	group: string;
	cellLabel: (row: number, column: number) => string;
	scoreOptions: ReturnType<typeof useScoreOptions>;
}): ActivityCopy => {
	const { item, t, actor, user, successor, group, cellLabel, scoreOptions } = options;
	const placement = item.placement;

	switch (item.type) {
		case graphql.TalentReviewActivityType.Moved: {
			const marker = item.user ? <Avatar size={AVATAR_SIZE} user={item.user} /> : null;
			const text = placement?.removed
				? t('talentReviews.v2.activity.removed', { actor, user })
				: placement?.previous
					? t('talentReviews.v2.activity.moved', {
							actor,
							user,
							from: cellLabel(placement.previous.row, placement.previous.column),
							cell: cellLabel(placement.row, placement.column),
						})
					: t('talentReviews.v2.activity.placed', {
							actor,
							user,
							cell: placement ? cellLabel(placement.row, placement.column) : '',
						});
			const score = scoreOptions.find((option) => option.value === placement?.score)?.title;
			if (placement?.removed || (!placement?.motivation && !score)) {
				return { text, marker };
			}

			return {
				text,
				marker,
				details: (
					<ActivityRow.Details label={t('talentReviews.v2.activity.showMotivation')}>
						{placement?.motivation && <ActivityRow.Quote>{placement.motivation}</ActivityRow.Quote>}
						{score && (
							<ActivityRow.Quote>
								{t('talentReviews.v2.activity.score', { score })}
							</ActivityRow.Quote>
						)}
					</ActivityRow.Details>
				),
			};
		}
		case graphql.TalentReviewActivityType.Submitted:
			return {
				text: t('talentReviews.v2.activity.submitted', { actor }),
				marker: (
					<ActivityRow.Marker color={tokens.success.icon}>
						<UserCheckIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.Reverted:
			return {
				text: t('talentReviews.v2.activity.reverted', { actor }),
				marker: (
					<ActivityRow.Marker color={tokens.warning.icon}>
						<UndoIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.Corrected:
			return {
				text: t('talentReviews.v2.activity.corrected', { actor }),
				marker: (
					<ActivityRow.Marker color={tokens.warning.icon}>
						<PencilIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.Cleared:
			return {
				text: t('talentReviews.v2.activity.cleared', { actor }),
				marker: (
					<ActivityRow.Marker>
						<CircleRemoveIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.MemberAdded:
			return {
				text: item.integration
					? t('talentReviews.v2.activity.memberAddedByIntegration', {
							user,
							group,
							integration: item.integration,
						})
					: t('talentReviews.v2.activity.memberAdded', { actor, user, group }),
				marker: (
					<ActivityRow.Marker>
						{item.integration ? <SyncIcon /> : <UserPlusIcon />}
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.MemberRemoved:
			return {
				text: item.integration
					? t('talentReviews.v2.activity.memberRemovedByIntegration', {
							user,
							group,
							integration: item.integration,
						})
					: t('talentReviews.v2.activity.memberRemoved', { actor, user, group }),
				marker: (
					<ActivityRow.Marker>
						{item.integration ? <SyncIcon /> : <CircleRemoveIcon />}
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.PlacementHelp:
			return {
				text: t('talentReviews.v2.activity.placementHelp', { actor, user }),
				marker: (
					<ActivityRow.Marker color={tokens.info.icon}>
						<HelpIcon />
					</ActivityRow.Marker>
				),
				details: item.answers?.length ? (
					<ActivityRow.Details label={t('talentReviews.v2.activity.showAnswers')}>
						{item.answers.map((answer, index) => (
							<ActivityRow.Quote key={index}>
								{t('talentReviews.v2.activity.answer', {
									question: answer.question,
									answer: answer.answer,
								})}
							</ActivityRow.Quote>
						))}
					</ActivityRow.Details>
				) : undefined,
			};
		case graphql.TalentReviewActivityType.RetentionRiskChanged:
			return {
				text: item.retentionRisk
					? t('talentReviews.v2.activity.retentionRiskChanged', {
							actor,
							user,
							risk: t(retentionRiskLabelKey(item.retentionRisk as RetentionRiskLevel)),
						})
					: t('talentReviews.v2.activity.retentionRiskCleared', { actor, user }),
				marker: (
					<ActivityRow.Marker color={tokens.warning.icon}>
						<WarningIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.SuccessorAdded:
			return {
				text: t('talentReviews.v2.activity.successorAdded', { actor, user, successor }),
				marker: (
					<ActivityRow.Marker>
						<UserPlusIcon />
					</ActivityRow.Marker>
				),
			};
		case graphql.TalentReviewActivityType.SuccessorRemoved:
			return {
				text: t('talentReviews.v2.activity.successorRemoved', { actor, user, successor }),
				marker: (
					<ActivityRow.Marker>
						<CircleRemoveIcon />
					</ActivityRow.Marker>
				),
			};
		default:
			return { text: '', marker: <ActivityRow.Marker /> };
	}
};

/**
 * One row of the activity list.
 * @param props component props
 * @param props.item the activity
 * @param props.template the grid's template, for cell labels
 * @param props.groupName the group's name, for joining/leaving texts
 */
export default function TalentReviewActivityRow(props: {
	item: Item;
	template: graphql.TalentReviewTemplate;
	groupName: string;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const scoreOptions = useScoreOptions();
	const { item } = props;

	const cellLabel = (row: number, column: number) =>
		localize(props.template.cells[row]?.[column]?.label)?.text ?? `${row + 1}/${column + 1}`;
	const timeAgo =
		Math.abs(moment().diff(item.createdAt, 'seconds')) < JUST_NOW_SECONDS
			? t('talentReviews.v2.activity.justNow')
			: moment(item.createdAt).fromNow();
	const actor = item.integration
		? t('talentReviews.v2.activity.integrationActor', { integration: item.integration })
		: name(item.createdBy);
	const user = name(item.user);
	const successor = name(item.successor);
	const { text, marker, details } = activityCopy({
		item,
		t,
		actor,
		user,
		successor,
		group: props.groupName,
		cellLabel,
		scoreOptions,
	});

	return (
		<ActivityRow
			marker={marker}
			meta={timeAgo}
			metaTooltip={moment(item.createdAt).format('LLL')}
			details={details}
		>
			{emphasizeNames(text, [actor, user, successor])}
		</ActivityRow>
	);
}
